// src/store/index.ts
import type { Feature } from '../types/global.types';

import { create } from 'zustand';

import { getLogger } from '@/lib/logger/logger';

import i18n from '../i18n';

const logger = getLogger('store');

/** ───────────────────────────── 유틸: 언어/로드/카테고리 검사 ───────────────────────────── */
// ✅ P0-3 FIX: Use i18n.language directly instead of detectLanguageFromRegion
// This ensures we always use the current i18n language state, not cached region
const getLang = (): 'ko' | 'en' | 'ja' => {
  const lang = i18n.language;
  logger.debug('[Store] getLang:', { lang });
  if (lang === 'en') return 'en';
  if (lang === 'ja') return 'ja';
  return 'ko';
};

type SamplesConfig = { SDK_EXAMPLES?: Record<string, Feature> };

const loadSamplesFromPublic = async (
  sdk: 'hwp' | 'hcell',
  language = getLang(),
): Promise<SamplesConfig> => {
  const res = await fetch(`/samples/${sdk}-sdk/${language}/samples.json`, {
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} while fetching ${sdk} samples`);
  return (await res.json()) as SamplesConfig;
};

const loadSamplesFromModule = async (sdk: string, language = getLang()): Promise<SamplesConfig> => {
  const mod = (await import(`../features/${sdk}-sdk/config/samples/${language}/samples.json`)) as {
    default: SamplesConfig;
  };
  return mod.default;
};

const isFeatureOfSDK = (feature: Feature | null | undefined, sdk: string) =>
  !!feature && feature.category === sdk;

/** ───────────────────────────── 기본 기능 선택자(HWP/HCELL) ────────────────────────────── */
const pickDefaultHwpFeature = (cfg: SamplesConfig): Feature | null => {
  const insertText = cfg.SDK_EXAMPLES?.insertText;
  return insertText ? { ...insertText, category: insertText.category } : null;
};

const pickDefaultHcellFeature = (cfg: SamplesConfig): Feature | null => {
  const map = cfg.SDK_EXAMPLES ?? {};
  const firstId = Object.keys(map)[0];
  const f = firstId ? map[firstId] : undefined;
  return f ? { ...f, category: f.category } : null;
};

/** ───────────────────────────── HWP 기본값 로드(+KO 폴백) ──────────────────────────────── */
const loadDefaultHwpWithFallback = async (): Promise<Feature | null> => {
  try {
    const lang = getLang();
    logger.info('Loading default HWP feature for:', lang);
    const cfg = await loadSamplesFromPublic('hwp', lang);
    const picked = pickDefaultHwpFeature(cfg);
    if (picked) return picked;
    logger.warn('No insertText feature in primary language');
  } catch (e) {
    logger.error('Primary HWP default load failed:', e);
  }

  // 폴백: ko
  try {
    logger.info('Falling back to Korean default for HWP');
    const cfgKo = await loadSamplesFromPublic('hwp', 'ko');
    const pickedKo = pickDefaultHwpFeature(cfgKo);
    if (pickedKo) return pickedKo;
    logger.warn('No insertText feature in Korean fallback');
  } catch (e2) {
    logger.error('Korean fallback failed for HWP:', e2);
  }

  return null;
};

/** ───────────────────────────── URL → 초기 SDK 판단 (기존 로직 유지) ───────────────────── */
const getInitialSDKFromURL = (): string => {
  const pathname = window.location.pathname;

  if (pathname.includes('/hcell-sdk')) return 'hcell';
  if (pathname.includes('/spreadsheet-sdk')) return 'hcell';
  if (pathname.includes('/docscompare-sdk')) return 'docdiff';
  if (pathname.includes('/ocr-sdk')) return 'ocr';
  if (pathname.includes('/hwp-sdk')) return 'hwp';
  if (pathname.includes('/dataloader')) return 'dataloader';

  // 기본값은 hcell (해외향 기본값으로 깜빡임 방지)
  return 'hcell';
};

/** ───────────────────────────── Zustand 스토어 ───────────────────────────── */
interface AppState {
  // UI 상태
  currentSDK: string | null;
  selectedFeature: Feature | null;

  // 액션
  setCurrentSDK: (sdk: string | null) => void;
  setSelectedFeature: (feature: Feature | null) => void;
  initializeDefaultFeature: () => Promise<void>;
  reloadSelectedFeature: () => Promise<void>;
}

export const useAppStore = create<AppState>((set, get) => ({
  // 초기 상태 - URL 기반으로 설정
  currentSDK: getInitialSDKFromURL(),
  selectedFeature: null,

  // 액션
  setCurrentSDK: (sdk: string | null) => set({ currentSDK: sdk }),
  setSelectedFeature: (feature: Feature | null) => set({ selectedFeature: feature }),

  // 기본 기능 초기화 (인지 복잡도 개선)
  initializeDefaultFeature: async () => {
    const { selectedFeature, currentSDK } = get();

    // 디버그용 로그 - 필요시만 활성화
    // logger.info('initializeDefaultFeature called:', {
    //   currentSDK,
    //   selectedFeatureCategory: selectedFeature?.category,
    // });

    if (!currentSDK) return; // 가드

    // 이미 해당 SDK의 기능이면 아무것도 안 함
    if (isFeatureOfSDK(selectedFeature, currentSDK)) return;

    // SDK별 핸들러 매핑
    const handlers: Record<string, () => Promise<Feature | null>> = {
      hwp: async () => loadDefaultHwpWithFallback(),
      hcell: async () => {
        try {
          const lang = getLang();
          logger.info('[Store] Loading HCELL default feature with lang:', lang);
          const cfg = await loadSamplesFromPublic('hcell', lang);
          return pickDefaultHcellFeature(cfg);
        } catch (e) {
          logger.error('Failed to load default HCELL feature:', e);
          return null;
        }
      },
    };

    const loader = handlers[currentSDK];
    if (!loader) return; // docdiff/ocr 등은 기본 기능 자동선택 없음

    const defaultFeature = await loader();
    if (defaultFeature) {
      // 기본 기능 설정 로그
      logger.debug(`Setting default ${currentSDK.toUpperCase()} feature:`, defaultFeature.id);
      set({ selectedFeature: defaultFeature });
    }
  },

  // 현재 선택된 기능 다시 로드 (언어 변경 시)
  reloadSelectedFeature: async () => {
    const { selectedFeature, currentSDK } = get();
    if (!selectedFeature || !currentSDK) return;

    logger.info('reloadSelectedFeature called:', {
      currentSDK,
      selectedFeatureId: selectedFeature.id,
      language: i18n.language,
    });

    try {
      // ✅ FIX: 언어 변경 시에는 지역 감지를 무시하고 현재 i18n 언어를 직접 사용
      const lang = (i18n.language ?? 'ko') as 'ko' | 'en' | 'ja';
      const cfg: SamplesConfig =
        currentSDK === 'hwp' || currentSDK === 'hcell'
          ? await loadSamplesFromPublic(currentSDK, lang)
          : await loadSamplesFromModule(currentSDK, lang);

      const updated = cfg.SDK_EXAMPLES?.[selectedFeature.id];
      if (updated) {
        set({ selectedFeature: { ...updated, category: selectedFeature.category } });
        logger.info('Updated feature:', updated.id);
      } else {
        logger.warn('Feature not found in new language:', selectedFeature.id);
      }
    } catch (error) {
      logger.error('Failed to reload selected feature:', error);
    }
  },
}));
